import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import Footer from '../components/Footer'
import { useUser } from '../contexts/UserContext'
import { ProfileSkeleton } from '../components/LoadingSkeleton'
import {
  ArrowLeft,
  Briefcase,
  Mail,
  UserCircle,
  MapPin,
  Phone,
  Info,
  GraduationCap,
  BookOpen,
  Pencil,
  Award,
  Calendar,
  ExternalLink,
} from 'lucide-react'
import { motion } from 'framer-motion'

export default function Profile() {
  const { userInfo, loading } = useUser()
  const [fullUserData, setFullUserData] = useState(null)
  const [certificates, setCertificates] = useState([])
  const [loadingFullData, setLoadingFullData] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchFullUserData = async () => {
      if (!userInfo?.id) return

      setLoadingFullData(true)
      try {
        // Fetch user data
        const { data, error } = await supabase
          .from('users')
          .select('experience, education, skills')
          .eq('id', userInfo.id)
          .single()

        if (error) {
          console.error('Error fetching full user data:', error)
        } else {
          setFullUserData(data)
        }

        // Fetch certificates
        const { data: certsData, error: certsError } = await supabase
          .from('certificates')
          .select('*')
          .eq('user_id', userInfo.id)
          .order('issue_date', { ascending: false })

        if (certsError) {
          console.error('Error fetching certificates:', certsError)
        } else {
          setCertificates(certsData || [])
        }
      } catch (error) {
        console.error('Error loading full user data:', error)
      } finally {
        setLoadingFullData(false)
      }
    }

    fetchFullUserData()
  }, [userInfo])

  if (loading || loadingFullData) {
    return (
      <div className="min-h-screen bg-white relative overflow-hidden p-4 sm:p-6 lg:p-8">
        {/* Floating Gradient Orbs Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-emerald-500/30 via-green-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-bl from-cyan-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-green-500/20 via-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.02) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(16, 185, 129, 0.02) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="pt-24 max-w-5xl mx-auto px-4 relative z-10">
          <ProfileSkeleton />
        </div>
      </div>
    )
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-white relative overflow-hidden p-4 sm:p-6 lg:p-8">
        {/* Floating Gradient Orbs Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-emerald-500/30 via-green-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-bl from-cyan-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        </div>
        <div className="pt-24 text-center text-red-500 relative z-10">
          Gagal memuat data pengguna.
        </div>
      </div>
    )
  }

  // Gabungkan data dari userInfo dan fullUserData
  const mergedUserData = {
    ...userInfo,
    experience: Array.isArray(fullUserData?.experience) 
      ? fullUserData.experience 
      : (Array.isArray(userInfo?.experience) ? userInfo.experience : []),
    education: Array.isArray(fullUserData?.education)
      ? fullUserData.education
      : (Array.isArray(userInfo?.education) ? userInfo.education : []),
    skills: fullUserData?.skills || userInfo?.skills
  }

  // Debug log

  return (
    <div className="min-h-screen bg-white relative overflow-hidden p-4 sm:p-6 lg:p-8">
      {/* Floating Gradient Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-emerald-500/30 via-green-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-bl from-cyan-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-green-500/20 via-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-float-slow"></div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(16, 185, 129, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header Banner */}
          <div className="relative backdrop-blur-xl bg-gradient-to-r from-emerald-600/90 via-green-600/90 to-cyan-600/90 p-6 sm:p-8 text-white rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 animate-pulse"></div>
                
                <div className="relative z-10">
                  <div className="flex justify-between items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-lg transition-all duration-200 group hover:scale-105"
                        title="Back to Dashboard"
                      >
                        <ArrowLeft size={18} className="sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" /> 
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">My Profile</h1>
                        <p className="text-blue-100 text-xs sm:text-sm mt-1 flex items-center gap-2">
                          <UserCircle size={14} className="sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Informasi lengkap profil Anda</span>
                          <span className="sm:hidden">Profil Anda</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => navigate('/edit-profile')}
                        className="flex-1 sm:flex-none bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl shadow-lg text-sm sm:text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 hover:scale-105"
                      >
                        <Pencil size={16} className="sm:w-4 sm:h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => navigate('/cv-preview')}
                        className="flex-1 sm:flex-none bg-white hover:bg-blue-50 text-blue-700 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl shadow-lg text-sm sm:text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 hover:scale-105"
                      >
                        <BookOpen size={16} className="sm:w-4 sm:h-4" />
                        <span>Preview CV</span>
                      </button>
                    </div>
                  </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative backdrop-blur-xl bg-white/70 rounded-b-3xl shadow-2xl border border-white/40 overflow-hidden">
          {/* Shimmer Effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
          
          <div className="p-6 sm:p-8 relative z-10">
            {/* Main Info Layout */}
            <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* Avatar & Basic Info */}
          <div className="flex flex-col items-center space-y-4">
            {/* Avatar with decorative ring */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
              <div className="relative w-40 h-40 rounded-full border-4 border-white shadow-2xl overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200">
                <img
                  src={
                    userInfo.photo
                      ? userInfo.photo
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          userInfo.full_name || 'User'
                        )}&background=0D8ABC&color=fff`
                  }
                  alt="Avatar"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
            
            {/* User Info Card */}
            <div className="text-center space-y-2 w-full">
              <h2 className="text-xl font-bold text-gray-800 tracking-tight">
                {userInfo.degree_prefix && `${userInfo.degree_prefix} `}
                {userInfo.full_name}
                {userInfo.degree_suffix && `, ${userInfo.degree_suffix}`}
              </h2>
              {(userInfo.degree_prefix || userInfo.degree_suffix) && (
                <div className="flex items-center justify-center gap-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                    <GraduationCap size={12} />
                    {userInfo.degree_prefix && userInfo.degree_suffix 
                      ? `${userInfo.degree_prefix} & ${userInfo.degree_suffix}`
                      : userInfo.degree_prefix || userInfo.degree_suffix
                    }
                  </span>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-gray-600 text-sm bg-gray-50 px-3 py-2 rounded-lg">
                <Mail size={14} />
                <p className="truncate">{userInfo.email}</p>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs shadow-md">
                <UserCircle size={14} />
                <span className="capitalize">{userInfo.role}</span>
              </div>
            </div>
          </div>

          {/* Detail Info */}
          <div className="md:col-span-2 space-y-6">
            <InfoBlock
              icon={<Briefcase size={20} />}
              label="Pekerjaan"
              value={userInfo.job}
            />

            <InfoBlock
              icon={<Info size={20} />}
              label="Ringkasan"
              value={
                userInfo.summary || 'Belum ada ringkasan yang ditambahkan.'
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock
                icon={<Phone size={18} />}
                label="Telepon"
                value={userInfo.phone}
              />
              <InfoBlock
                icon={<MapPin size={18} />}
                label="Lokasi"
                value={userInfo.location}
              />
            </div>

            {/* Skills */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-5 rounded-xl border border-blue-100">
              <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Info size={16} className="text-white" />
                </div>
                Keahlian / Skills
              </h4>
              {mergedUserData.skills ? (
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(mergedUserData.skills)
                    ? mergedUserData.skills
                    : mergedUserData.skills.split(',')
                  )
                    .map((skill) => skill.trim())
                    .filter(Boolean)
                    .map((skill, idx) => (
                      <span
                        key={idx}
                        className="bg-white text-blue-700 text-xs px-3 py-1.5 rounded-lg font-medium shadow-sm border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all"
                      >
                        {skill}
                      </span>
                    ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm">
                  Belum ada keahlian yang ditambahkan.
                </p>
              )}
            </div>

            {/* Experience */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-5 rounded-xl border border-blue-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Briefcase size={16} className="text-white" />
                </div>
                Pengalaman Kerja
              </h3>
              {Array.isArray(mergedUserData.experience) && mergedUserData.experience.length > 0 ? (
                <div className="space-y-3">
                  {mergedUserData.experience.map((exp, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm hover:shadow-lg hover:border-blue-400 transition-all duration-200 flex gap-4 group"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                        <Briefcase size={20} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-blue-700 text-base mb-1">
                          {exp.position || 'Posisi tidak diketahui'}
                        </h4>
                        <p className="text-sm text-gray-800 font-semibold mb-1">
                          {exp.company || 'Perusahaan tidak diketahui'}
                        </p>
                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                          <span>📅</span>
                          {exp.period || 'Periode tidak diketahui'}
                        </p>
                        {exp.description && (
                          <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-200">
                            {exp.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm bg-white p-4 rounded-lg border border-dashed border-gray-300">
                  Belum ada pengalaman kerja yang ditambahkan.
                </p>
              )}
            </div>

            {/* Education */}
            <div className="bg-gradient-to-br from-blue-50 to-pink-50 p-5 rounded-xl border border-blue-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-pink-600 flex items-center justify-center">
                  <GraduationCap size={16} className="text-white" />
                </div>
                Pendidikan
              </h3>
              {Array.isArray(mergedUserData.education) && mergedUserData.education.length > 0 ? (
                <div className="space-y-3">
                  {mergedUserData.education.map((edu, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm hover:shadow-lg hover:border-blue-400 transition-all duration-200 flex gap-4 group"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-pink-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                        <GraduationCap size={20} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-blue-700 text-base mb-1">
                          {edu.degree || 'Gelar tidak diketahui'}
                        </h4>
                        <p className="text-sm text-gray-800 font-semibold mb-1">
                          {edu.school || 'Sekolah tidak diketahui'}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <span>🎓</span>
                          {edu.year || 'Tahun tidak diketahui'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm bg-white p-4 rounded-lg border border-dashed border-gray-300">
                  Belum ada riwayat pendidikan yang ditambahkan.
                </p>
              )}
            </div>

            {/* Certificates */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border border-green-100">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Award size={16} className="text-white" />
                </div>
                Sertifikat
              </h3>
              {Array.isArray(certificates) && certificates.length > 0 ? (
                <div className="space-y-3">
                  {certificates.map((cert, index) => (
                    <div
                      key={index}
                      className="bg-white p-4 rounded-xl border border-green-200 shadow-sm hover:shadow-lg hover:border-green-400 transition-all duration-200 group"
                    >
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                          <Award size={20} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-green-700 text-base mb-1">
                            {cert.certificate_name}
                          </h4>
                          <p className="text-sm text-gray-800 font-semibold mb-2">
                            {cert.issuing_organization}
                          </p>
                          {cert.description && (
                            <p className="text-xs text-gray-600 mb-2">
                              {cert.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            {cert.issue_date && (
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(cert.issue_date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })}
                              </span>
                            )}
                            {cert.expiry_date && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <Calendar size={12} />
                                Berlaku hingga {new Date(cert.expiry_date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })}
                              </span>
                            )}
                            {cert.credential_id && (
                              <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                                ID: {cert.credential_id}
                              </span>
                            )}
                          </div>
                          {cert.credential_url && (
                            <a 
                              href={cert.credential_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-xs text-green-600 hover:text-green-700 font-semibold hover:underline"
                            >
                              <ExternalLink size={12} />
                              Verifikasi Sertifikat
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm bg-white p-4 rounded-lg border border-dashed border-gray-300">
                  Belum ada sertifikat yang ditambahkan.
                </p>
              )}
            </div>
          </div>
        </div>
        </div>
        </div>
      </motion.div>
      </div>
      <Footer />
    </div>
  )
}

// Komponen untuk Info Block
function InfoBlock({ icon, label, value }) {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-5 rounded-xl shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 group">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-700 mb-1">{label}</h3>
          <p className="text-gray-800 text-sm leading-relaxed break-words">{value || 'Belum diisi'}</p>
        </div>
      </div>
    </div>
  )
}
